import React, {
  ChangeEvent,
  FC,
  HTMLAttributes,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import styles from './Dropdown.module.scss';
import { useOnClickOutside } from 'hooks';
import { Error } from 'components';
import { IconButtonWrapper } from 'components/ui/index';
import { t } from 'utils';

export interface IOption {
  value: string;
  label: string;
  translationKey?: string;
  disabled?: boolean;
}

export interface IDropdown extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: string | string[];
  hardCodedValue?: string | string[];
  onChange: (value: string) => void;
  options: IOption[];
  isShowSelected?: boolean;
  placeholder?: string;
  label?: string;
  useRelativeTop?: boolean;
  intl?: any;
  error?: string;
  modifiedSinceLastSubmit?: boolean;
  submitError?: string;
  isAllSelected?: boolean;
  disabled?: boolean;
  isSmallList?: boolean;
}

const Dropdown: FC<IDropdown> = (props) => {
  const {
    value,
    label,
    error,
    options,
    disabled,
    className,
    isSmallList,
    placeholder,
    submitError,
    isAllSelected,
    hardCodedValue,
    isShowSelected,
    useRelativeTop,
    onChange = () => {},
    modifiedSinceLastSubmit,
    ...otherProps
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const selectedOption = options?.find((option) => option.value === (hardCodedValue ?? value));
  // if option does not have label, use translationKey field
  const selectedValue = selectedOption?.label ?? selectedOption?.translationKey;
  const rootClassNames = [
    classNames(styles.root, className, {
      [styles['small-list']]: isSmallList,
      [styles.isOpen]: isOpen,
      [styles.focused]: selectedValue,
      [styles.disabled]: disabled,
      [styles.error]: !modifiedSinceLastSubmit && (error ?? submitError),
    }),
  ];
  const inputClassName = classNames(styles.input, { [styles.cursorPointer]: isShowSelected });
  if (useRelativeTop) rootClassNames.push(styles['dropdown-relative-top']);
  const rootRef = useRef(null);
  const iconClassName = classNames(styles.icon, 'fas fa-sort');
  const filteredOptions = useMemo(
    () =>
      options?.filter(({ label }) =>
        (label ?? '').toLowerCase().includes((filter ?? '').toLowerCase())
      ),
    [filter, options]
  );

  const optionsList = useMemo(() => (isShowSelected ? options : filteredOptions), [
    filteredOptions,
    isShowSelected,
    options,
  ]);

  const handleControlClick = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleCloseList = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleFilterClear = useCallback(() => {
    setFilter('');
  }, []);

  const handleOptionClick = useCallback(
    (value) => {
      onChange(value);
      handleCloseList();
      handleFilterClear();
    },
    [handleCloseList, onChange, handleFilterClear]
  );

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  }, []);

  useOnClickOutside(rootRef, handleCloseList);

  const list = (optionsList ?? []).map(
    ({ value: optionValue, label, disabled, translationKey }, i) => (
      <div
        className={classNames(styles.option, {
          [styles.isDisabled]: value.includes(optionValue) || !!disabled || isAllSelected,
        })}
        onClick={() => handleOptionClick(optionValue)}
        key={optionValue ?? i}
        role='presentation'
      >
        {t(translationKey ?? label)}
      </div>
    )
  );

  return (
    <>
      <div className={rootClassNames.join(' ')} ref={rootRef} {...otherProps}>
        <label className={styles.control} role='presentation'>
          {label && <span className={styles.labelText}>{t(label)}</span>}
          <input
            value={isShowSelected ? t(selectedValue) : filter}
            onClick={handleControlClick}
            onChange={handleInputChange}
            className={inputClassName}
            readOnly={isShowSelected}
            type={'text'}
            placeholder={placeholder ? t(placeholder) : undefined}
            autoComplete={'off'}
            autoCorrect={'off'}
            autoCapitalize={'none'}
            aria-autocomplete={'list'}
          />
          <IconButtonWrapper
            className={classNames(styles.clearIcon, { 'd-none': !filter })}
            onClick={handleFilterClear}
            icon={'times'}
          />
          <span className={iconClassName} />
        </label>
        <div className={styles.list}>
          {list.length > 0 && list}
          {list.length < 1 && (
            <div
              className={classNames(styles.option, {
                [styles.isDisabled]: true,
                [styles.notFound]: true,
              })}
              onClick={() => {}}
              role='presentation'
            >
              {t('global.noResults')}
            </div>
          )}
        </div>
      </div>
      <Error className='mt-2' isError={!modifiedSinceLastSubmit && (submitError ?? error)}>
        {submitError ?? error}
      </Error>
    </>
  );
};

export default Dropdown;
